import React, { useState, useEffect } from 'react';
import { Table, Card, Button, Tag, Space, Typography, Modal, Form, Input, Select, Switch, message, Row, Col, Alert, Tooltip, Badge } from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  UserOutlined,
  EyeOutlined,
  ReloadOutlined,
  ExportOutlined,
  TeamOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import { safeGetRelativeTime, safeFormatTimestamp } from '../../utils/timezone';
import {
  usernameRules,
  emailRules,
  fullNameRules,
  passwordRules,
  passwordHelpText,
} from '../../utils/validationRules';
import logger from '../../services/logger';

const { Title, Text } = Typography;
const { Option } = Select;

const Users = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [passwordPolicy, setPasswordPolicy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [form] = Form.useForm();

  const [submitting, setSubmitting] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    inactive: 0
  });

  useEffect(() => {
    fetchUsers();
    fetchCustomers();
    fetchRoles();
    fetchPasswordPolicy();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/v1/users');
      logger.info('Users API response:', response.data);
      logger.info('Is array:', Array.isArray(response.data));
      setUsers(Array.isArray(response.data) ? response.data : []);

      // Calculate statistics
      const userData = Array.isArray(response.data) ? response.data : [];
      const userStats = {
        total: userData.length,
        active: userData.filter(u => u.is_active).length,
        inactive: userData.filter(u => !u.is_active).length
      };
      setStats(userStats);
    } catch (error) {
      logger.error('Users fetch error:', error);
      message.error('Kullanıcılar yüklenirken hata oluştu');
      setUsers([]); // Hata durumunda boş array set et
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await axios.get('/api/v1/customers');
      setCustomers(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      logger.error('Customers fetch error:', error);
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await axios.get('/api/v1/roles');
      setRoles(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      logger.error('Roles fetch error:', error);
    }
  };

  const fetchPasswordPolicy = async () => {
    try {
      const response = await axios.get('/api/v1/profile/password-requirements');
      setPasswordPolicy(response.data);
    } catch (error) {
      logger.error('Password policy fetch error:', error);
    }
  };

  const handleAdd = () => {
    setEditingUser(null);
    setSelectedRole(null);
    form.resetFields();
    form.setFieldsValue({ is_active: true, role: 'operator' });
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingUser(record);
    setSelectedRole(record.role?.name || record.role);
    const customerIds = record.customers ? record.customers.map(c => c.id) : [];
    form.setFieldsValue({
      ...record,
      role: record.role?.name || record.role,
      password: '', // Şifreyi form'da gösterme
      customer_ids: customerIds
    });
    setModalVisible(true);
  };

  const handleDelete = async (id) => {
    try {
      // Kendini silmeye karşı koruma
      if (id === user?.id) {
        message.error('Kendi hesabınızı silemezsiniz');
        return;
      }
      
      const confirmed = window.confirm('Bu kullanıcıyı silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.');
      if (!confirmed) return;
      
      await axios.delete(`/api/v1/users/${id}`);
      message.success('Kullanıcı başarıyla silindi');
      fetchUsers();
    } catch (error) {
      logger.error('User deletion error:', error);
      
      let errorMessage = 'Kullanıcı silinirken hata oluştu';
      
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.response?.status === 403) {
        errorMessage = 'Bu işlem için yetkiniz bulunmuyor.';
      } else if (error.response?.status === 401) {
        errorMessage = 'Oturum süreniz dolmuş. Lütfen tekrar giriş yapın.';
      } else if (error.response?.status === 404) {
        errorMessage = 'Kullanıcı bulunamadı.';
      }
      
      message.error(errorMessage);
    }
  };

  const handleSubmit = async (values) => {
    try {
      setSubmitting(true);
      
      if (editingUser) {
        // Güncelleme sırasında şifre boşsa gönderme
        if (!values.password) {
          delete values.password;
        }
        await axios.put(`/api/v1/users/${editingUser.id}`, values);
        message.success('Kullanıcı başarıyla güncellendi');
      } else {
        await axios.post('/api/v1/users', values);
        message.success('Kullanıcı başarıyla eklendi');
      }
      setModalVisible(false);
      form.resetFields();
      fetchUsers();
    } catch (error) {
      logger.error('User operation error:', error);
      
      // Backend'den gelen detaylı hata mesajını göster
      let errorMessage = 'İşlem başarısız';
      
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.status === 400) {
        errorMessage = 'Geçersiz veri. Lütfen tüm alanları doğru şekilde doldurun.';
      } else if (error.response?.status === 409) {
        errorMessage = 'Bu kullanıcı adı veya e-posta adresi zaten kullanılıyor.';
      } else if (error.response?.status === 403) {
        errorMessage = 'Bu işlem için yetkiniz bulunmuyor.';
      } else if (error.response?.status === 401) {
        errorMessage = 'Oturum süreniz dolmuş. Lütfen tekrar giriş yapın.';
      }
      
      message.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const columns = [
    {
      title: 'Kullanıcı Adı',
      dataIndex: 'username',
      key: 'username',
      render: (text, record) => (
        <Space>
          <UserOutlined style={{ color: record.is_active ? '#52c41a' : '#d9d9d9' }} />
          <span style={{ fontWeight: 'bold' }}>{text}</span>
        </Space>
      ),
    },
    {
      title: 'Ad Soyad',
      dataIndex: 'full_name',
      key: 'full_name',
    },
    {
      title: 'E-posta',
      dataIndex: 'email',
      key: 'email',
      render: (email) => <a href={`mailto:${email}`}>{email}</a>,
    },
    {
      title: 'Rol',
      dataIndex: 'role',
      key: 'role',
      render: (role) => {
        if (!role) return <Tag>-</Tag>;
        const roleName = typeof role === 'object' ? role.display_name || role.name : role;
        const roleColor = typeof role === 'object' ? role.color : null;
        return <Tag color={roleColor || 'default'}>{roleName}</Tag>;
      },
      filters: roles.map(r => ({ text: r.display_name, value: r.name })),
      onFilter: (value, record) => (record.role?.name || record.role) === value,
    },
    {
      title: 'Durum',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (active) => (
        <Badge 
          status={active ? 'success' : 'error'} 
          text={active ? 'Aktif' : 'Pasif'} 
        />
      ),
      filters: [
        { text: 'Aktif', value: true },
        { text: 'Pasif', value: false },
      ],
      onFilter: (value, record) => record.is_active === value,
    },
    {
      title: 'Son Giriş',
      dataIndex: 'last_login',
      key: 'last_login',
      render: (date) => {
        if (!date) return <span style={{ color: '#999' }}>Hiç giriş yapmamış</span>;
        
        try {
          // Timezone utility fonksiyonlarını kullan (UTC -> Europe/Istanbul)
          const relativeTime = safeGetRelativeTime(date);
          const fullDate = safeFormatTimestamp(date);
          
          // Renklendirme için relative time metnini kontrol et
          let color = '#999';
          if (relativeTime.includes('Az önce') || relativeTime.includes('dakika')) {
            color = '#52c41a';
          } else if (relativeTime.includes('saat') || relativeTime.includes('Dün')) {
            color = '#52c41a';
          } else if (relativeTime.includes('gün')) {
            // Gün sayısını çıkar
            const days = parseInt(relativeTime.match(/\d+/)?.[0] || '0');
            if (days < 7) {
              color = '#fa8c16';
            } else {
              color = '#ff4d4f';
            }
          } else {
            color = '#ff4d4f';
          }
          
          return (
            <Tooltip title={fullDate}>
              <span style={{ color }}>{relativeTime}</span>
            </Tooltip>
          );
        } catch (error) {
          logger.error('Last login date parse error:', error, date);
          return <span style={{ color: '#999' }}>Hata</span>;
        }
      },
      sorter: (a, b) => new Date(a.last_login || 0) - new Date(b.last_login || 0),
    },
    {
      title: 'İşlemler',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Tooltip title="Profil Detayları">
            <Button 
              icon={<EyeOutlined />} 
              onClick={() => navigate(`/users/${record.id}`)}
              type="text"
            />
          </Tooltip>
          <Tooltip title="Düzenle">
            <Button 
              icon={<EditOutlined />} 
              onClick={() => handleEdit(record)}
              type="text"
            />
          </Tooltip>
          <Tooltip title="Sil">
            <Button 
              icon={<DeleteOutlined />} 
              danger 
              onClick={() => handleDelete(record.id)}
              type="text"
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <Title level={2}>
          <UserOutlined /> Kullanıcılar
        </Title>
      </div>

      {/* User Stats Summary */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
          <Space size="large" wrap>
            <span>
              <InfoCircleOutlined style={{ color: '#1890ff', marginRight: 6 }} />
              <Text style={{ marginRight: 4 }}>Toplam</Text>
              <Text strong style={{ fontSize: 18 }}>{stats.total}</Text>
            </span>
            <span>
              <Badge status="success" />
              <Text style={{ marginRight: 4 }}>Aktif</Text>
              <Text strong style={{ color: '#3f8600', fontSize: 18 }}>{stats.active}</Text>
            </span>
            {stats.inactive > 0 && (
              <span>
                <Badge status="warning" />
                <Text style={{ marginRight: 4 }}>Pasif</Text>
                <Text strong style={{ color: '#fa8c16', fontSize: 18 }}>{stats.inactive}</Text>
              </span>
            )}
          </Space>
          <Space size="middle" wrap>
            {roles.filter(r => {
              const count = (Array.isArray(users) ? users : []).filter(u => (u.role?.name || u.role) === r.name).length;
              return count > 0;
            }).map(r => {
              const count = (Array.isArray(users) ? users : []).filter(u => (u.role?.name || u.role) === r.name).length;
              return (
                <span key={r.name}>
                  <Badge color={r.color || '#d9d9d9'} />
                  <Text style={{ marginRight: 4 }}>{r.display_name}</Text>
                  <Text strong style={{ fontSize: 16 }}>{count}</Text>
                </span>
              );
            })}
          </Space>
        </div>
      </Card>

      {/* Alerts */}
      {stats.inactive > 0 && (
        <Alert
          message={`${stats.inactive} pasif kullanıcı var`}
          description="Pasif kullanıcıları kontrol edin."
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      {/* Action Buttons and Search */}
      <Card style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Space>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
              Yeni Kullanıcı Ekle
            </Button>
            <Button icon={<ReloadOutlined />} onClick={fetchUsers} loading={loading}>
              Yenile
            </Button>
            <Button icon={<ExportOutlined />}>
              Dışa Aktar
            </Button>
          </Space>
          <div style={{ minWidth: 400 }}>
            <Input.Search
              placeholder="Kullanıcı adı, e-posta, isim ara..."
              allowClear
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: '100%', minWidth: 300 }}
            />
          </div>
        </div>
      </Card>

      {/* Users Table */}
      <Card>
        <Table
          columns={columns}
          dataSource={(Array.isArray(users) ? users : []).filter(u => {
            if (!searchText) return true;
            const s = searchText.toLowerCase();
            return (
              u.username?.toLowerCase().includes(s) ||
              u.email?.toLowerCase().includes(s) ||
              u.full_name?.toLowerCase().includes(s) ||
              (u.role?.name || u.role)?.toLowerCase().includes(s)
            );
          })}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 20,
            showSizeChanger: true,
            showTotal: (total) => `Toplam ${total} kullanıcı`,
          }}
          rowClassName={(record) => {
            if (!record.is_active) return 'inactive-row';
            return '';
          }}
        />
      </Card>

      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <UserOutlined style={{ color: '#1890ff' }} />
            {editingUser ? 'Kullanıcı Düzenle' : 'Yeni Kullanıcı Ekle'}
          </div>
        }
        open={modalVisible}
        onCancel={() => {
          if (!submitting) {
            setModalVisible(false);
            setEditingUser(null);
            form.resetFields();
          }
        }}
        footer={null}
        width={800}
        closable={!submitting}
        maskClosable={!submitting}
        destroyOnClose
      >
        <Form form={form} onFinish={handleSubmit} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="username"
                label="Kullanıcı Adı"
                rules={usernameRules()}
              >
                <Input
                  prefix={<UserOutlined />}
                  placeholder="Kullanıcı adı girin"
                  disabled={!!editingUser}
                />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                name="full_name"
                label="Ad Soyad"
                rules={fullNameRules()}
              >
                <Input placeholder="Ad soyad girin" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="email"
                label="E-posta"
                rules={emailRules()}
              >
                <Input placeholder="ornek@email.com" />
              </Form.Item>
            </Col>
            
            <Col span={12}>
              <Form.Item
                name="is_active"
                label="Hesap Durumu"
                valuePropName="checked"
                help="Kullanıcının sisteme giriş yapabilmesi için aktif olmalıdır"
              >
                <Switch
                  checkedChildren="Aktif"
                  unCheckedChildren="Pasif"
                />
              </Form.Item>
            </Col>
          </Row>
          
          <Form.Item
            name="role"
            label="Rol"
            rules={[{ required: true, message: 'Rol gerekli' }]}
          >
            <Select
              placeholder="Rol seçin"
              onChange={(value) => {
                setSelectedRole(value);
                if (value !== 'customer') {
                  form.setFieldsValue({ customer_ids: [] });
                }
              }}
            >
              {roles
                .filter(r => r.is_active)
                .filter(r => user?.role?.name === 'admin' || r.name !== 'admin')
                .sort((a, b) => b.hierarchy_level - a.hierarchy_level)
                .map(r => (
                  <Option key={r.name} value={r.name}>
                    <Space>
                      <Badge color={r.color || '#d9d9d9'} />
                      {r.display_name}
                    </Space>
                  </Option>
                ))
              }
            </Select>
          </Form.Item>

          {selectedRole === 'customer' && (
            <>
              <Alert
                message="Müşteri Rolü Bilgisi"
                description="Bu kullanıcı sadece seçtiğiniz müşterilere ait cihazları ve verileri görebilecektir."
                type="info"
                showIcon
                icon={<InfoCircleOutlined />}
                style={{ marginBottom: 16 }}
              />
              <Form.Item
                name="customer_ids"
                label="Müşteriler"
                rules={[
                  {
                    required: true,
                    message: 'Müşteri rolü için en az bir müşteri seçilmeli'
                  }
                ]}
                help="Bu kullanıcının erişebileceği müşterileri seçin (birden fazla seçilebilir)"
              >
                <Select
                  mode="multiple"
                  placeholder="Müşteri seçin"
                  showSearch
                  filterOption={(input, option) =>
                    option.children.toLowerCase().includes(input.toLowerCase())
                  }
                  maxTagCount={3}
                  maxTagPlaceholder={(omittedValues) => `+${omittedValues.length} müşteri`}
                >
                  {customers.map(c => (
                    <Option key={c.id} value={c.id}>
                      <Space>
                        <TeamOutlined style={{ color: c.is_active ? '#52c41a' : '#d9d9d9' }} />
                        {c.name}
                        {!c.is_active && <Tag color="default">Pasif</Tag>}
                      </Space>
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </>
          )}
          
          <Form.Item
            name="password"
            label={editingUser ? 'Şifre (Değiştirmek istemiyorsanız boş bırakın)' : 'Şifre'}
            rules={passwordRules(passwordPolicy, { required: !editingUser, forEdit: !!editingUser })}
            help={passwordHelpText(passwordPolicy, !!editingUser)}
          >
            <Input.Password placeholder="Şifre girin" />
          </Form.Item>
          
          {editingUser && (
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item label="Son Giriş">
                  <Input
                    value={editingUser.last_login ? new Date(editingUser.last_login).toLocaleString('tr-TR') : 'Hiç giriş yapmamış'}
                    disabled
                    style={{ color: '#666' }}
                  />
                </Form.Item>
              </Col>
            </Row>
          )}
          
          {editingUser && (
            <Alert
              message="Düzenleme Bilgisi"
              description="Kullanıcı adı değiştirilemez. Şifre alanını boş bırakırsanız mevcut şifre korunur."
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}
          
          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setModalVisible(false)} disabled={submitting}>
                İptal
              </Button>
              <Button type="primary" htmlType="submit" loading={submitting}>
                {editingUser ? 'Güncelle' : 'Ekle'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Users;
